import { ErrorSeverity, ErrorCategory, ErrorCode } from './types';
import type { ErrorContext, BaseErrorOptions } from './types';

/**
 * Base class for all structured errors in the LinguGeek platform
 * Provides consistent error structure and serialization
 */
export class BaseError extends Error {
  public readonly code: ErrorCode;
  public readonly category: ErrorCategory;
  public readonly severity: ErrorSeverity;
  public readonly context: ErrorContext;
  public readonly timestamp: Date;
  public readonly isOperational: boolean = true;
  declare public readonly cause?: Error;

  constructor(options: BaseErrorOptions) {
    super(options.message);

    // Set the prototype explicitly for proper instanceof checks
    Object.setPrototypeOf(this, BaseError.prototype);

    this.name = this.constructor.name;
    this.code = options.code;
    this.category = options.category;
    this.severity = options.severity || ErrorSeverity.Error;
    this.context = options.context || {};
    this.timestamp = new Date();

    // Preserve the cause chain
    if (options.cause) {
      this.cause = options.cause;
      // Include the original stack trace if available
      if (options.cause.stack && !this.context.stack) {
        this.context.stack = options.cause.stack;
      }
    }

    // Capture stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }

  /**
   * Convert error to JSON for logging/telemetry
   */
  toJSON(): Record<string, unknown> {
    return {
      name: this.name,
      message: this.message,
      code: this.code,
      category: this.category,
      severity: this.severity,
      timestamp: this.timestamp.toISOString(),
      context: this.context,
      stack: this.stack,
      isOperational: this.isOperational,
    };
  }

  /**
   * Convert error to API response format
   */
  toResponse(): {
    success: false;
    error: {
      code: ErrorCode;
      message: string;
      category: ErrorCategory;
      severity: ErrorSeverity;
      context?: Partial<ErrorContext>;
    };
  } {
    // Create a safe context for public API responses
    const safeContext: Partial<ErrorContext> = {
      requestId: this.context.requestId,
      timestamp: this.context.timestamp || this.timestamp,
    };

    // Only include safe metadata in production
    if (process.env.NODE_ENV === 'development') {
      safeContext.userId = this.context.userId;
      safeContext.method = this.context.method;
      safeContext.url = this.context.url;
      safeContext.route = this.context.route;
    }

    return {
      success: false,
      error: {
        code: this.code,
        message: this.message,
        category: this.category,
        severity: this.severity,
        context: safeContext,
      },
    };
  }

  /**
   * Get HTTP status code based on error category and code
   */
  getHttpStatusCode(): number {
    // Check if status code is explicitly set in context
    if (this.context.statusCode) {
      return this.context.statusCode;
    }

    // Default status codes based on category
    switch (this.category) {
      case ErrorCategory.Authentication:
        return 401;
      case ErrorCategory.Authorization:
        return 403;
      case ErrorCategory.Validation:
        return 400;
      case ErrorCategory.Network:
        return 503;
      case ErrorCategory.Database:
        return 500;
      case ErrorCategory.Payment:
        return 402;
      case ErrorCategory.ThirdParty:
        return 502;
      case ErrorCategory.Configuration:
        return 500;
      case ErrorCategory.User:
        return 400;
      case ErrorCategory.System:
        return 503;
      case ErrorCategory.Internal:
      default:
        return 500;
    }
  }

  /**
   * Check if error is retryable
   */
  isRetryable(): boolean {
    const retryableCategories = [
      ErrorCategory.Network,
      ErrorCategory.ThirdParty,
      ErrorCategory.System,
    ];

    const retryableCodes = [
      ErrorCode.NETWORK_TIMEOUT,
      ErrorCode.NETWORK_CONNECTION_REFUSED,
      ErrorCode.THIRD_PARTY_SERVICE_UNAVAILABLE,
      ErrorCode.SYSTEM_OVERLOADED,
      ErrorCode.SYSTEM_RESOURCE_EXHAUSTED,
    ];

    return (
      retryableCategories.includes(this.category) ||
      retryableCodes.includes(this.code)
    );
  }

  /**
   * Static method to check if an error is a BaseError instance
   */
  static isBaseError(error: unknown): error is BaseError {
    return error instanceof BaseError;
  }

  /**
   * Create error from unknown cause
   */
  static fromUnknown(
    cause: unknown,
    fallbackOptions: Omit<BaseErrorOptions, 'cause'>
  ): BaseError {
    if (BaseError.isBaseError(cause)) {
      return cause;
    }

    if (cause instanceof Error) {
      return new BaseError({
        ...fallbackOptions,
        cause,
      });
    }

    return new BaseError({
      ...fallbackOptions,
      message: `${fallbackOptions.message}: ${String(cause)}`,
    });
  }
}
